import * as MiniZinc from 'minizinc';
// TODO: Enable when --no-bundle bug is fixed
// import './src/minizinc-worker.js';
import './src/minizinc.wasm';
import './src/minizinc.data';
import './problem.mzn';
import type { Group } from './script';

export type InjectionData = {
    xCount: number;
    yCount: number;
    seatingPlanIsGap: boolean[][];
    groupsArray: (Group & { id: string })[];
};

export type Solution = { [group: string]: { x: number, y: number }[] | number | undefined, _objective?: number };

export class MiniZincExtra {
    private static initialising = false;
    private static initialised = false;

    public static mzn: string;
    public static CONFIG = {
        options: {
            solver: 'chuffed',
            // 'time-limit': 10000,
            statistics: true,
            "output-objective": true,
            "intermediate-solutions": true,
            "all-solutions": true,
            "compiler-statistics": true,
            "solver-statistics": true,
        }
    };

    public static async init() {
        if (this.initialising || this.initialised) { return; }
        this.initialising = true;

        console.log(`${navigator.hardwareConcurrency} logical processors detected`);
        const webWorkerCount = Math.max(1, navigator.hardwareConcurrency - 1) ?? 3;
        console.log(`Using ${webWorkerCount} web workers for MiniZinc`);
        await MiniZinc.init({
            numWorkers: webWorkerCount,
        }).then(() => console.log('MiniZinc initialized'));
        await MiniZinc.version().then(version => console.log(version));
        await MiniZinc.solvers().then(solvers => console.log("Solvers:", { solvers }));
        this.mzn = await fetch('./problem.mzn').then(res => res.text());

        this.initialised = true;
    }

    public static newModel() {
        if (!this.initialised) { throw new Error("MiniZinc not initialised yet!"); }
        return new MiniZincModel();
    }
}

class MiniZincModel {
    private model: MiniZinc.Model;
    private optimisedObjective: number | null = null;

    constructor() {
        this.model = new MiniZinc.Model();
    }

    private getInjectedMZn(
        {
            xCount,
            yCount,
            seatingPlanIsGap,
            groupsArray,
        }: InjectionData,
        enumerateSolutions?: boolean,
    ): string {
        if (this.optimisedObjective === null && enumerateSolutions) {
            throw new Error("Cannot enumerate solutions without first optimising to get the objective value!");
        }

        // <Gap Injection>
        // array[int] of Gap: GAPS = [(x: <x>, y: <y>), ...];
        const gapInjection = `array[int] of Gap: GAPS = [${seatingPlanIsGap.flatMap((row, y) => row.map((isGap, x) => isGap ? ` (x: ${x + 1}, y: ${y + 1})` : null)).filter(v => v !== null).join(', ')}];`;

        // <Group Injection>
        // array[1..<n>] of var Seat: group<n>;
        // array[int] of Group: groups = [group<n>, ...];
        // constraint group < n > [<i>].x = <x>;
        // constraint group < n > [<i>].y = <y>;
        const groupInjection = [
            ...groupsArray.map((group, i) => `array[1..${group.size}] of var Seat: group${i};`),
            `array[int] of Group: groups = [${groupsArray.map((_, i) => `group${i}`).join(', ')}];`,
        ].join('\n');

        // <xyCount Injection>
        // int: X_COUNT = <xCount>;
        // int: Y_COUNT = <yCount>;
        const xyCountInjection = `int: X_COUNT = ${xCount};\nint: Y_COUNT = ${yCount};`;

        let injectedMZn = MiniZincExtra.mzn
            .replace(/% <xyCount Injection>(.|\s)*? <\/xyCount Injection>/, xyCountInjection)
            .replace(/% <Gap Injection>(.|\s)*? <\/Gap Injection>/, gapInjection)
            .replace(/% <Group Injection>(.|\s)*? <\/Group Injection>/, groupInjection);

        if (enumerateSolutions) {
            // <optimisation>
            // solve minimize badness;

            // <enumeration>
            // constraint badness = <_objective>;
            // solve satisfy;

            const enumerationInjection = [
                `constraint badness = ${this.optimisedObjective};`,
                `solve satisfy;`,
            ].join('\n');

            injectedMZn = injectedMZn
                .replace(/% <optimisation>(.|\s)*? <\/optimisation>/, '')
                .replace(/% <enumeration>(.|\s)*? <\/enumeration>/, enumerationInjection);
        }

        console.log("Injections:", { gapInjection, groupInjection, xyCountInjection });
        console.log("Injected MiniZinc:", { injectedMZn });
        return injectedMZn;
    }

    // new helper: wrap a SolveProgress so callers can intercept .on events and .then/.catch/.finally
    private wrapSolveProgress(orig: MiniZinc.SolveProgress) {
        type Handler = (...args: any[]) => void;
        const listeners = new Map<string, Set<Handler>>();

        const emit = (event: string, ...args: any[]) => {
            const set = listeners.get(event);
            if (!set) { return; }
            for (const h of Array.from(set)) {
                try { h(...args); } catch (e) { console.error('Wrapped handler error', e); }
            }
        };

        // re-emit all events from original to our listeners (allow interception point here)
        const proxiedEvents = ['checker', 'error', 'exit', 'statistics', 'timestamp', 'trace', 'warning', 'solution', 'status'];
        for (const ev of proxiedEvents) {
            orig.on(ev as any, (...args: any[]) => {
                // interception: custom logic can be added here if desired
                // e.g. transform payload, filter, logging, etc.
                // then emit to wrapper listeners
                emit(ev, ...args);
            });
        }

        const wrapper: any = {
            on: (event: string, handler: Handler) => {
                if (!listeners.has(event)) listeners.set(event, new Set());
                listeners.get(event)!.add(handler);
                return wrapper;
            },
            off: (event: string, handler?: Handler) => {
                if (!listeners.has(event)) return wrapper;
                if (handler) listeners.get(event)!.delete(handler);
                else listeners.delete(event);
                return wrapper;
            },
            // allow callers to emit their own events into the wrapper
            emit: (event: string, ...args: any[]) => {
                emit(event, ...args);
                return wrapper;
            },
            // allow chaining .then/.catch/.finally while giving callers a chance to register handlers on our wrapper
            then: (onFulfilled?: (value: any) => any, onRejected?: (reason: any) => any) => {
                return orig.then((result: any) => {
                    // emit a special wrapper lifecycle event before resolving to caller's then
                    emit('wrapped-then', result);
                    return onFulfilled ? onFulfilled(result) : result;
                }, (err: any) => {
                    emit('wrapped-reject', err);
                    return onRejected ? onRejected(err) : Promise.reject(err);
                });
            },
            catch: (onRejected?: (reason: any) => any) => {
                return wrapper.then(undefined, onRejected);
            },
            finally: (onFinally?: () => void) => {
                // original may support finally; if not, fallback
                if (typeof (orig as any).finally === 'function') {
                    return (orig as any).finally(() => {
                        emit('wrapped-finally');
                        if (onFinally) onFinally();
                    });
                } else {
                    // best-effort: attach to then
                    return wrapper.then((r: any) => { emit('wrapped-finally'); if (onFinally) onFinally(); return r; }, (e: any) => { emit('wrapped-finally'); if (onFinally) onFinally(); return Promise.reject(e); });
                }
            },
        };

        return wrapper as MiniZinc.SolveProgress & { on: (e: string, h: Handler) => any; off: (e: string, h?: Handler) => any; };
    }

    public solve(injectionData: InjectionData) {
        performance.mark('solve-start');

        this.model.addString(this.getInjectedMZn(injectionData, false));
        const optimisationProgress = this.model.solve(MiniZincExtra.CONFIG) as MiniZinc.SolveProgress;

        // keep the original internal listeners (they will still run on 'origProgress')
        optimisationProgress.on('checker', checker => { console.log("Checker:", checker); });
        optimisationProgress.on('error', error => { console.error("Error:", error); });
        optimisationProgress.on('exit', exit => { console.log("Exit:", exit); });
        optimisationProgress.on('statistics', statistics => { console.log("Statistics:", statistics); });
        optimisationProgress.on('timestamp', timestamp => { console.log("Timestamp:", timestamp); });
        optimisationProgress.on('trace', trace => { console.log("Trace:", trace); });
        optimisationProgress.on('warning', warning => { console.warn("Warning:", warning); });

        optimisationProgress.on('solution', solution => {
            const json = solution.output?.json as Solution;
            console.log("Solution:", json);
            //! internal hook for solution
        });
        optimisationProgress.on('status', status => {
            console.log("Status:", status.status);
            //! internal hook for status
        });

        // wrap and return the wrapper so callers can intercept events and promise resolution
        const wrappedSolveProgress = this.wrapSolveProgress(optimisationProgress);

        // chain original final-handling to support optimisation -> enumeration transition,
        // but use the original promise so internal logic can still replace the model when needed.
        optimisationProgress.then(result => {
            performance.mark('solve-end');
            performance.measure('solve', 'solve-start', 'solve-end');
            const measure = performance.getEntriesByName('solve')[0]!;
            console.log("Performance Measure:", measure);

            console.log("Full JSON:", result);
            const json = result.solution?.output?.json as Solution;

            console.log("Result:", json);
            console.log("Statistics:", result.statistics);
            console.log("Status:", result.status);
            //! internal post-process
            if (result.status === 'OPTIMAL_SOLUTION') {
                this.optimisedObjective = json._objective ?? null;
                console.log("Optimised Objective:", this.optimisedObjective);
                console.log("Doing enumeration of all optimal solutions...");

                this.model = new MiniZinc.Model();
                this.model.addString(this.getInjectedMZn(injectionData, true));

                const enumerationProgress = this.model.solve(MiniZincExtra.CONFIG);
                // simply replace origProgress reference and re-emit events from new progress into wrapper
                (async () => {
                    // forward events from enumeration progress to wrapper
                    const events = ['checker', 'error', 'exit', 'statistics', 'timestamp', 'trace', 'warning', 'solution', 'status'] as const;
                    for (const ev of events) {
                        enumerationProgress.on(ev as any, (...args: any[]) => {
                            // emit the same event on original and wrapper listeners
                            try { (optimisationProgress as any).on(ev, () => { }); } catch { /* noop */ }
                            (wrappedSolveProgress as any).on(ev, () => { }); // ensure key exists (no-op)
                            // but invoke wrapper listeners directly:
                            // (wrapped as any) has no emit, so use the internal mapping by calling the private emit via a known event:
                            // easier: just call a public 'on' with same event and the stored listeners won't be called; instead, re-use wrapper's emit by invoking wrapper's internal behavior is non-trivial here.
                            // To keep this change small and reliable, just log and let origProgress.then replacement continue to handle enumeration via returning new promise below.
                            console.log(`Enumeration event ${ev}:`, ...args);
                            (wrappedSolveProgress as any).emit?.(ev, ...args);
                        });
                    }
                })();

                setTimeout(() => { (wrappedSolveProgress as any).emit?.('status', { status: 'ENUMERATING' }); }, 0);

                // return the enumeration progress so callers that awaited origProgress.then get the final promise
                return enumerationProgress;
            }
        });

        setTimeout(() => { (wrappedSolveProgress as any).emit?.('status', { status: 'OPTIMISING' }); }, 0);

        return wrappedSolveProgress;
    }
}